// Tax calculation utilities

export interface TaxCalculationResult {
  subtotal: number
  taxAmt: number
  total: number
}

const TAX_RATES: Record<string, number> = {
  I: 0.1, // Input Tax (GST 10%)
  E: 0, // Exempt
  Z: 0, // Zero Rated
  O: 0, // Out of Scope
  R: 0.05, // Reduced Rate (5%)
}

export function getTaxRate(taxCode: string): number {
  return TAX_RATES[taxCode] ?? 0.1
}

export function calculateTax(subtotal: number, taxCode: string): number {
  const rate = getTaxRate(taxCode)
  return subtotal * rate
}

export function calculateTotal(subtotal: number, taxCode: string): number {
  const taxAmt = calculateTax(subtotal, taxCode)
  return subtotal + taxAmt
}

export function handleTaxCodeChange(
  currentSubtotal: number,
  currentTaxAmt: number,
  currentTotal: number,
  newTaxCode: string,
  oldTaxCode: string,
): TaxCalculationResult {
  // When tax code changes, we need to recalculate based on the total (tax-inclusive)
  // or the subtotal (tax-exclusive) depending on business rules

  const oldRate = getTaxRate(oldTaxCode)
  const newRate = getTaxRate(newTaxCode)

  // Option 1: Keep the total the same, adjust subtotal and tax
  // This is common when the invoice total is fixed
  if (newRate === 0) {
    // If new tax code is exempt/zero, subtotal = total
    return {
      subtotal: currentTotal,
      taxAmt: 0,
      total: currentTotal,
    }
  }

  // Calculate new subtotal from total (assuming total stays the same)
  const newSubtotal = currentTotal / (1 + newRate)
  const newTaxAmt = currentTotal - newSubtotal

  return {
    subtotal: Math.round(newSubtotal * 100) / 100,
    taxAmt: Math.round(newTaxAmt * 100) / 100,
    total: currentTotal,
  }
}

export function recalculateLineItem(
  quantity: number,
  unitPrice: number,
  taxCode: string,
): TaxCalculationResult {
  const subtotal = quantity * unitPrice
  const taxAmt = calculateTax(subtotal, taxCode)
  const total = subtotal + taxAmt

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    taxAmt: Math.round(taxAmt * 100) / 100,
    total: Math.round(total * 100) / 100,
  }
}
